import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Session } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { useSectionAccess } from "@/hooks/useSectionAccess";
import { useIsAdmin } from "@/hooks/useUserRole";
import { useMenuConfig } from "@/hooks/useMenuConfig";
import { MENU_ICONS, type MenuItem } from "@/lib/menuConfig";
import { NavLink } from "@/components/NavLink";
import { Badge } from "@/components/ui/badge";
import {
  Sidebar, SidebarContent, SidebarGroup, SidebarGroupContent, SidebarGroupLabel, SidebarMenu,
  SidebarMenuButton, SidebarMenuItem, SidebarMenuSub, SidebarMenuSubItem, SidebarMenuSubButton,
  SidebarFooter, SidebarHeader, SidebarSeparator, useSidebar,
} from "@/components/ui/sidebar";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { ChevronRight, LogOut, UserCircle, Circle } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

const AppSidebar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAdmin } = useIsAdmin();
  const { canPath } = useSectionAccess();
  const { toast } = useToast();
  const { state } = useSidebar();
  const collapsed = state === "collapsed";
  const [session, setSession] = useState<Session | null>(null);
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({});
  const { config } = useMenuConfig();

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => setSession(session));
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_e, s) => setSession(s));
    return () => subscription.unsubscribe();
  }, []);

  const { data: userProfile } = useQuery({
    queryKey: ["current-user-profile", session?.user?.id],
    queryFn: async () => {
      if (!session?.user?.id) return null;
      const { data, error } = await supabase.from("profiles").select("full_name, email").eq("id", session.user.id).single();
      if (error) return null;
      return data;
    },
    enabled: !!session?.user?.id,
  });

  const { data: openIssuesCount } = useQuery({
    queryKey: ["open-issues-count"],
    queryFn: async () => {
      const { count } = await supabase.from("system_issues").select("*", { count: "exact", head: true }).eq("status", "open");
      return count || 0;
    },
  });

  const handleSignOut = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) toast({ title: "Error signing out", description: error.message, variant: "destructive" });
    else navigate("/auth");
  };

  const isActive = (path?: string) => !!path && (location.pathname === path || (path === "/projects" && location.pathname.startsWith("/project/")));
  const visible = (it: MenuItem) => !it.hidden && (it.external || !it.url || canPath(it.url));
  const childMatches = (it: MenuItem) => (it.children ?? []).some((c) => c.url && c.url !== "/" && location.pathname.startsWith(c.url));

  useEffect(() => {
    const next: Record<string, boolean> = {};
    config.sections.forEach((s) => s.items.forEach((it) => { if (it.children && childMatches(it)) next[it.id] = true; }));
    if (Object.keys(next).length) setOpenGroups((p) => ({ ...p, ...next }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, config]);

  const Icon = ({ name }: { name: string }) => { const C = MENU_ICONS[name] ?? Circle; return <C className="h-4 w-4" />; };

  const linkInner = (it: MenuItem) => (
    <>
      <Icon name={it.icon} />
      <span className="flex-1">{it.label}</span>
      {it.url === "/issues" && !!openIssuesCount && openIssuesCount > 0 && !collapsed && (
        <Badge variant="destructive" className="ml-auto text-xs px-1.5 py-0.5">{openIssuesCount}</Badge>
      )}
    </>
  );

  const renderLink = (it: MenuItem) =>
    it.external ? (
      <a href={it.url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2">{linkInner(it)}</a>
    ) : (
      <NavLink to={it.url || "/"} end={it.url === "/" || it.url === "/portal/company-admin"} className="flex items-center gap-2" activeClassName="bg-sidebar-accent text-sidebar-accent-foreground">{linkInner(it)}</NavLink>
    );

  const sections = config.sections
    .filter((s) => !s.hidden && (!s.adminOnly || isAdmin))
    .map((s) => ({
      ...s,
      items: s.items
        .map((it) => (it.children ? { ...it, children: it.children.filter(visible) } : it))
        .filter((it) => (it.children ? !it.hidden && it.children.length > 0 : visible(it))),
    }))
    .filter((s) => s.items.length > 0);

  return (
    <Sidebar collapsible="icon" className="border-r">
      <SidebarHeader className="px-2 py-2" />
      <SidebarContent className="pt-2">
        {sections.map((s, idx) => (
          <div key={s.id}>
            {idx > 0 && <SidebarSeparator />}
            <SidebarGroup>
              {s.label && <SidebarGroupLabel>{s.label}</SidebarGroupLabel>}
              <SidebarGroupContent>
                <SidebarMenu>
                  {s.items.map((it) =>
                    it.children ? (
                      <Collapsible key={it.id} open={!!openGroups[it.id]} onOpenChange={(o) => setOpenGroups((p) => ({ ...p, [it.id]: o }))} className="group/collapsible">
                        <SidebarMenuItem>
                          <CollapsibleTrigger asChild>
                            <SidebarMenuButton tooltip={it.label} isActive={childMatches(it)}>
                              <Icon name={it.icon} />
                              <span>{it.label}</span>
                              <ChevronRight className="ml-auto h-4 w-4 transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90" />
                            </SidebarMenuButton>
                          </CollapsibleTrigger>
                          <CollapsibleContent>
                            <SidebarMenuSub>
                              {it.children.map((c) => (
                                <SidebarMenuSubItem key={c.id}>
                                  <SidebarMenuSubButton asChild isActive={isActive(c.url)}>{renderLink(c)}</SidebarMenuSubButton>
                                </SidebarMenuSubItem>
                              ))}
                            </SidebarMenuSub>
                          </CollapsibleContent>
                        </SidebarMenuItem>
                      </Collapsible>
                    ) : (
                      <SidebarMenuItem key={it.id}>
                        <SidebarMenuButton asChild isActive={isActive(it.url)} tooltip={it.label}>{renderLink(it)}</SidebarMenuButton>
                      </SidebarMenuItem>
                    ),
                  )}
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          </div>
        ))}

        {!isAdmin && (
          <>
            <SidebarSeparator />
            <SidebarGroup>
              <SidebarGroupContent>
                <SidebarMenu>
                  <SidebarMenuItem>
                    <SidebarMenuButton asChild isActive={isActive("/profile")} tooltip="Profile">
                      <NavLink to="/profile" className="flex items-center gap-2" activeClassName="bg-sidebar-accent text-sidebar-accent-foreground">
                        <UserCircle className="h-4 w-4" />
                        <span>Profile</span>
                      </NavLink>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          </>
        )}
      </SidebarContent>

      {/* Footer with User Info and Sign Out */}
      <SidebarFooter className="border-t">
        {/* User Identity Section */}
        {!collapsed && (session || userProfile) && (
          <div className="px-3 py-2 text-sm">
            <p className="font-medium text-foreground truncate">
              {userProfile?.full_name || "User"}
            </p>
            <p className="text-xs text-muted-foreground truncate">
              {userProfile?.email || session?.user?.email}
            </p>
          </div>
        )}
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton
              onClick={handleSignOut}
              tooltip="Sign Out"
              className="text-destructive hover:text-destructive hover:bg-destructive/10"
            >
              <LogOut className="h-4 w-4" />
              <span>Sign Out</span>
            </SidebarMenuButton>
          </SidebarMenuItem>
          {!collapsed && (
            <SidebarMenuItem>
              <SidebarMenuButton
                onClick={async () => {
                  if (!window.confirm("Sign out of the dashboard on all your devices?")) return;
                  await supabase.auth.signOut({ scope: "global" });
                  navigate("/auth");
                }}
                className="text-xs text-muted-foreground"
              >
                <span>Sign out of all devices</span>
              </SidebarMenuButton>
            </SidebarMenuItem>
          )}
        </SidebarMenu>
      </SidebarFooter>
    </Sidebar>
  );
};

export { AppSidebar };
